import * as Sentry from "@sentry/react";
import { BrowserTracing } from "@sentry/tracing";
import axios from "axios";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface MonitoringUser {
  id: string;
  username: string;
  email?: string;
  role?: string;
  branch?: string;
}

type ErrorSource = "api-error" | "ui-error" | "network-error";

export interface CaptureErrorOptions {
  source: ErrorSource;
  componentName?: string;
  endpoint?: string;
  statusCode?: number;
  extra?: Record<string, unknown>;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const MF_NAME = process.env.NEXT_PUBLIC_MF_NAME || "mf-bribrain-main-desktop";
const DSN = process.env.NEXT_PUBLIC_GLITCHTIP_DSN;
const ENV = process.env.NEXT_PUBLIC_ENV || "develop";
const RELEASE = process.env.NEXT_PUBLIC_RELEASE;

// ─── Initialization ──────────────────────────────────────────────────────────

export function initMonitoring(): void {
  if (typeof window === "undefined" || !DSN) {
    return;
  }

  if (Sentry.getCurrentHub().getClient()) {
    return;
  }

  Sentry.init({
    dsn: DSN,
    environment: ENV,
    release: RELEASE ? `${MF_NAME}@${RELEASE}` : undefined,
    integrations: [new BrowserTracing()],
    tracesSampleRate: 0.2,
    sampleRate: 1.0,
    autoSessionTracking: false,
    initialScope: {
      tags: { mf_name: MF_NAME },
    },
    beforeSend(event, hint) {
      const error = hint?.originalException;

      if (error instanceof TypeError && error.message === "Failed to fetch") {
        return null;
      }

      if (
        error instanceof Error &&
        (error.name === "ChunkLoadError" ||
          error.message.includes("Loading chunk"))
      ) {
        return null;
      }

      if (
        error instanceof DOMException &&
        error.name === "AbortError"
      ) {
        return null;
      }

      return event;
    },
  });
}

// ─── User Identification ─────────────────────────────────────────────────────

export function identifyUser(user: MonitoringUser): void {
  Sentry.setUser({
    id: user.id,
    username: user.username,
    email: user.email,
  });
  if (user.role) Sentry.setTag("user.role", user.role);
  if (user.branch) Sentry.setTag("user.branch", user.branch);
}

export function clearUser(): void {
  Sentry.setUser(null);
}

// ─── Error Capture ───────────────────────────────────────────────────────────

export function captureError(
  error: unknown,
  options: CaptureErrorOptions
): void {
  Sentry.withScope((scope) => {
    scope.setTag("source", options.source);
    scope.setTag("mf_name", MF_NAME);
    if (options.componentName) scope.setTag("componentName", options.componentName);
    if (options.endpoint) scope.setTag("endpoint", options.endpoint);
    if (options.statusCode) scope.setTag("statusCode", options.statusCode);

    if (options.extra) {
      Object.entries(options.extra).forEach(([key, value]) => {
        scope.setExtra(key, value);
      });
    }

    if (error instanceof Error) {
      Sentry.captureException(error);
    } else {
      Sentry.captureException(new Error(String(error)));
    }
  });
}

export function captureMessage(
  message: string,
  options?: Partial<CaptureErrorOptions>
): void {
  Sentry.withScope((scope) => {
    scope.setTag("mf_name", MF_NAME);
    if (options?.source) scope.setTag("source", options.source);
    if (options?.componentName) scope.setTag("componentName", options.componentName);
    Sentry.captureMessage(message);
  });
}

// ─── Axios Interceptor ───────────────────────────────────────────────────────

export function setupAxiosInterceptors(): void {
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error?.response?.status;
      const url = error?.config?.url || "unknown";
      const method = (error?.config?.method || "unknown").toUpperCase();
      const responseMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.response?.statusText ||
        error?.message ||
        "Unknown error";

      const isLoginEndpoint = url.includes("/login");
      const isExpectedAuthError = status === 401 && isLoginEndpoint;

      if (!isExpectedAuthError) {
        const sentryError = new Error(
          `${method} ${url} [${status || "network"}]: ${responseMessage}`
        );
        sentryError.name = status ? `ApiError ${status}` : "NetworkError";

        captureError(sentryError, {
          source: status ? "api-error" : "network-error",
          endpoint: url,
          statusCode: status,
          extra: {
            method,
            responseData: error?.response?.data
              ? typeof error.response.data === "string"
                ? error.response.data.substring(0, 500)
                : error.response.data
              : undefined,
          },
        });
      }

      return Promise.reject(error);
    }
  );
}

// ─── Re-export ErrorBoundary for MonitoringErrorBoundary ─────────────────────

export { ErrorBoundary } from "@sentry/react";
